import { Offer, OfferType, PartCore, PartId, PartOffer } from 'types/part';
import { OrgSupplier, PublicSupplier } from 'types/suppliers';
import { PurchaseRule, StockLocationLite } from 'types/inventory';
import { OrgPartSchema, V2OrgPartTypes } from 'types/clientV2/parts';
import { PriceModel } from 'types/purchasing';
import { CofactrPurchaseChargeType } from 'types/procurement';
import {
  DimensionClassOptions,
  UnitOfMeasureOptions,
} from 'types/unitsOfMeasure';

export type DecoratedOffer = Offer & {
  orgSupplier: OrgSupplier | undefined;
  publicSupplier?: PublicSupplier | undefined;
  part: PartCore | undefined;
};

export type DecoratedOfferGroup = {
  orgSupplier: OrgSupplier | undefined;
  publicSupplier?: PublicSupplier | undefined;
  part: PartCore | undefined;
  offerType: OfferType;
  offers: DecoratedOffer[];
  reportedStock: number;
  lead: number;
  bulkQuant?: number;
};

export enum ManagedByCofactrPreference {
  TRUE = 'true',
  FALSE = 'false',
  FROM_SUPPLIER = 'from_supplier',
}

export type OptimizePreferences = {
  quotesOverridePurchaseRule?: boolean;
  targetLead?: number;
  maxLead?: number;
  noBackorders?: boolean;
  avoidBackorders?: boolean;
  destinationLocation?: StockLocationLite | null;
  managedByCofactr?: ManagedByCofactrPreference;
  dropShip?: boolean;
};

export type LineOffer = {
  partId: PartId;
  offers: Offer[];
  v2OrgPartId?: OrgPartSchema['id'];
};

export type OfferRequestLine = {
  id: string;
  part: PartCore;
  altParts?: PartCore[];
  offers: LineOffer[];
  quant: number;
  purchaseRule?: PurchaseRule | null;
  preferences?: OptimizePreferences;
  dimensionUnit?: UnitOfMeasureOptions | null;
  dimensionClass?: DimensionClassOptions | null;
} & Pick<V2OrgPartTypes, 'v2OrgPart' | 'altV2OrgParts'>;

export type OptimizeOffersRequest = {
  lines: OfferRequestLine[];
  allowPublicSuppliers?: boolean;
  daysToWeeksCutoff?: number;
  betterThanNextBestPercentageThreshold?: number;
  betterThanNextBestDollarsThreshold?: number;
  minBuyablePercentage?: number;
  priceModel?: PriceModel | null;
};

export type OptimizeOffersMultipleRequest = {
  lines: { [id: string]: OfferRequestLine[] };
  allowPublicSuppliers?: boolean;
  daysToWeeksCutoff?: number;
  betterThanNextBestPercentageThreshold?: number;
  betterThanNextBestDollarsThreshold?: number;
  minBuyablePercentage?: number;
  priceModel?: PriceModel | null;
};

export type OfferLinesToUse = {
  id: string;
  actualPurchaseQuant: number;
  offers: DecoratedOffer[];
  unitPrice: number;
  totalPrice: number;
  isBackordered: boolean;
};

export type OptimizeOffersResponse = {
  lines: OfferLinesToUse[];
};

export type OffersToUse = Omit<OfferLinesToUse, 'id' | 'offers'> & {
  offers: (PartOffer | Offer | DecoratedOffer)[];
};

export type DecoratedOfferLine = {
  id: OfferRequestLine['id'];
  quant: OfferRequestLine['quant'];
  purchaseRule: OfferRequestLine['purchaseRule'];
  preferences: OfferRequestLine['preferences'];
  offers: DecoratedOffer[];
};

export type FilteredLineOffers = {
  id: OfferRequestLine['id'];
  quant: OfferRequestLine['quant'];
  preferences?: OfferRequestLine['preferences'];
  offerGroups: DecoratedOfferGroup[];
};

export type LandedCostLine = {
  price: number;
  description: string;
  type: CofactrPurchaseChargeType;
  excludeFromResult?: boolean;
};

export enum KittingEstimateChargeType {
  KITTING = 'kitting',
  REELING = 'reeling',
  SHIPPING = 'shipping',
}

export type KittingEstimateLine = {
  quant: number;
  unitPrice: number;
  totalPrice: number;
  description: string;
  type: KittingEstimateChargeType;
};

export type OptimizerBid = {
  lineId: string;
  partId: string;
  v2OrgPartId: OrgPartSchema['id'];
  bidQuant: number;
  selectedQuant?: number;
  selectedUnitPrice?: number;
  selectedTotalPrice?: number;
  bidTotalPrice: number;
  bidUnitPrice: number;
  bidTotalLandedPrice: number;
  bidUnitLandedPrice: number;
  offer: DecoratedOffer;
  shipsInLead: number;
  minLead: number;
  isReeled: boolean;
  isBackorder: boolean;
  isManagedByCofactr: boolean;
  landedCosts: LandedCostLine[];
};

export type BaseBid = {
  bidQuant: number;
  bidPrice: number;
  unitPrice: number;
  offer: DecoratedOffer;
};

export type OptimizerLineBids = {
  id: string;
  quant: number;
  bids: OptimizerBid[];
};

export type TariffRate = {
  coo: string[] | null;
  importCountry: string | null;
  tariffRate: number;
  htsus: string[] | null;
};

export type OptimizerLineResult = {
  id: string;
  quant: number;
  selectedBids: OptimizerBid[];
};

export type OptimizerSupplierResult = {
  publicSupplier: PublicSupplier | null;
  orgSupplier: OrgSupplier | null;
  supplierLandedCosts: LandedCostLine[];
  totalLineCosts: number;
  totalLandedCosts: number;
  selectedBids: OptimizerBid[];
};

export type OptimizerResult = {
  leadTime: number;
  totalLandedCosts: number;
  includedLines: OptimizerLineResult[];
  excludedLines?: OptimizerLineResult['id'][];
  suppliers: OptimizerSupplierResult[];
};

export type Supplier = OrgSupplier | PublicSupplier;

export enum LineStatus {
  NOT_BUYABLE = 'Not Buyable',
  BUYABLE = 'Buyable',
  BACKORDERED = 'Backordered',
  UNCERTAIN_LEAD = 'Uncertain Lead',
}

export enum NonBuyableReason {
  AVAILABLE_AT_DIFFERENT_LEAD_TIME = 'AVAILABLE_AT_DIFFERENT_LEAD_TIME',
  FILTERED_BY_PURCHASE_RULE = 'FILTERED_BY_PURCHASE_RULE',
  HAS_UNAPPROVED_QUOTES = 'HAS_UNAPPROVED_QUOTES',
  FILTERED_BY_DROPSHIP_RULE = 'FILTERED_BY_DROPSHIP_RULE',
  NO_BUYABLE_PARTS = 'NO_BUYABLE_PARTS',
  INSUFFICIENT_SUPPLIER_STOCK = 'INSUFFICIENT_SUPPLIER_STOCK',
}
